import React, { useState, useEffect } from "react";
import api from "@/api";
import TradeSignalModal from "@/components/modals/TradeSignalModal";
import { formatCurrency, decodeHtmlEntities } from "@/utils/formatters";

const TodoListPanel = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [filter, setFilter] = useState("active"); // active, all

  useEffect(() => {
    fetchSignals(true); // Initial load
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchSignals(false), 60000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchSignals = async (isInitialLoad = false) => {
    try {
      // Only show loading state on initial load, not on background refreshes
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      const params = {};
      if (filter === "active") {
        params.status = "proposed,accepted";
      }

      const response = await api.get("/trade-signals", { params });
      setSignals(response.data.signals || []);
    } catch (err) {
      console.error("Failed to fetch trade signals:", err);
      setError("Failed to load signals");
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const handleSignalClick = async (signal) => {
    try {
      // Fetch full signal details with linked intel
      const response = await api.get(`/trade-signals/${signal.id}`);
      setSelectedSignal(response.data);
    } catch (err) {
      console.error("Failed to fetch signal details:", err);
    }
  };

  const handleUpdateStatus = async (signalId, newStatus) => {
    try {
      await api.patch(`/trade-signals/${signalId}`, { status: newStatus });

      // Refresh signals list (background refresh)
      await fetchSignals(false);

      // Close modal
      setSelectedSignal(null);
    } catch (err) {
      console.error("Failed to update signal:", err);
      alert("Failed to update signal status");
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      proposed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      accepted:
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      executed:
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
      dismissed:
        "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      expired: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getSignalTypeColor = (type) => {
    return type === "BUY"
      ? "text-green-600 dark:text-green-400"
      : "text-red-600 dark:text-red-400";
  };

  return (
    <>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="p-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Trade Signals
              </h2>
              <button
                onClick={fetchSignals}
                disabled={loading}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                title="Refresh"
              >
                <svg
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter("active")}
                className={`px-3 py-1 text-xs font-medium rounded-md ${
                  filter === "active"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-1 text-xs font-medium rounded-md ${
                  filter === "all"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                All
              </button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="text-center py-4">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && signals.length === 0 && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && signals.length === 0 && (
            <div className="text-center py-8">
              <svg
                className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No {filter === "active" ? "active" : ""} signals yet
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Trade signals will appear here
              </p>
            </div>
          )}

          {/* Signals List */}
          {!loading && !error && signals.length > 0 && (
            <div className="space-y-2">
              {signals.map((signal) => (
                <div
                  key={signal.id}
                  onClick={() => handleSignalClick(signal)}
                  className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {signal.ticker}
                        </span>
                        <span
                          className={`text-sm font-semibold ${getSignalTypeColor(
                            signal.signal_type,
                          )}`}
                        >
                          {signal.signal_type}
                        </span>
                      </div>

                      {signal.suggested_price && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                          Target: {formatCurrency(signal.suggested_price)}
                        </p>
                      )}

                      {signal.rationale && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {decodeHtmlEntities(signal.rationale)}
                        </p>
                      )}
                    </div>

                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(
                        signal.status,
                      )}`}
                    >
                      {signal.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Signal Detail Modal */}
      {selectedSignal && (
        <TradeSignalModal
          signal={selectedSignal}
          onClose={() => setSelectedSignal(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </>
  );
};

export default TodoListPanel;
